import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Label from 'components/v2/forms/Label';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/20/solid';
import CircleButton from 'components/v2/buttons/CircleButton';

export default function MultiSelectTable({
  selectedColumnName,
  availableColumnName,
  columnNamesClassName,
  selectedItems,
  allItemsList,
  onSelect,
}) {
  const [required, setRequired] = useState(selectedItems);
  const [available, setAvailable] = useState([]);
  const [selectRequired, setSelectRequired] = useState([]);
  const [selectAvailable, setSelectAvailable] = useState([]);

  const findAvailable = (required) => {
    const req = new Set(required.map((req) => req.value));
    const availableDocs = allItemsList.filter((doc) => !req.has(doc.value));
    setAvailable(availableDocs);
  };

  useEffect(() => {
    findAvailable(required);
  }, [required]);

  useEffect(() => {
    setRequired(selectedItems);
    findAvailable(selectedItems);
  }, [selectedItems]);

  const addToRequired = (value) => {
    const newRequired = required.concat(value);
    setRequired(newRequired);
    onSelect(newRequired);
  };

  const removeFromRequired = () => {
    const remove = new Set(selectRequired.map((req) => req.value));
    const newRequired = required.filter((doc) => !remove.has(doc.value));
    setRequired(newRequired);
    onSelect(newRequired);
  };

  const handleEmptyingRequired = () => {
    setRequired([]);
    onSelect([]);
  };

  const handleSelectedRequired = (e) => {
    const newRequired = Array.from(e?.target?.selectedOptions, (option) => {
      const found = allItemsList.find((item) => item.value === option.value);
      return found
        ? {
            name: found.name,
            value: found.value,
          }
        : {
            name: option.value?.toUpperCase(),
            value: option.value,
          };
    });

    setSelectRequired(newRequired);
  };

  const handleSelectedAvailable = (e) => {
    const newAvailable = Array.from(e.target.selectedOptions, (option) => {
      const found = allItemsList.find((item) => item.value === option.value);
      return found
        ? {
            name: found.name,
            value: found.value,
          }
        : {
            name: option.value?.toUpperCase(),
            value: option.value,
          };
    });
    setSelectAvailable(newAvailable);
  };

  return (
    <div className="flex -mx-4 justify-between items-center overflow-x-auto">
      <div className="px-4">
        <Label labelClassName={classnames(columnNamesClassName)}>
          {selectedColumnName}
        </Label>
        <div className="py-2">
          <select
            className="border-gray-100 shadow text-sm h-80 w-auto sm:w-52 rounded-md overflow-auto"
            multiple
            onChange={handleSelectedRequired}
          >
            {required.map((doc) => (
              <option
                className="py-1 text-sm text-gray-700"
                key={doc.value}
                value={doc.value}
              >
                {doc.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="px-4 space-y-2">
        <CircleButton
          onClick={() => {
            addToRequired(available);
          }}
          size="small"
          className="mt-2"
        >
          <ChevronDoubleLeftIcon className="w-4 h-4" />
        </CircleButton>
        <CircleButton
          onClick={() => {
            addToRequired(selectAvailable);
          }}
          size="small"
          className="mt-2"
          color="brand"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </CircleButton>
        <CircleButton onClick={removeFromRequired} size="small">
          <ArrowRightIcon className="w-4 h-4" />
        </CircleButton>
        <CircleButton
          onClick={handleEmptyingRequired}
          size="small"
          className="mt-2"
        >
          <ChevronDoubleRightIcon className="w-4 h-4" />
        </CircleButton>
      </div>
      <div className="px-4">
        <Label labelClassName={classnames(columnNamesClassName)}>
          {availableColumnName}
        </Label>
        <div className="py-2">
          <select
            className="border-gray-100 shadow text-sm h-80 w-auto sm:w-52 rounded-md overflow-auto"
            multiple
            onChange={handleSelectedAvailable}
          >
            {available.map((doc) => (
              <option
                className="py-1 text-sm text-gray-700"
                key={doc.value}
                value={doc.value}
              >
                {doc.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

MultiSelectTable.propTypes = {
  selectedColumnName: PropTypes.string.isRequired,
  availableColumnName: PropTypes.string.isRequired,
  columnNamesClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.object),
  allItemsList: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func.isRequired,
};

MultiSelectTable.defaultProps = {
  selectedItems: [],
  allItemsList: [],
};
